import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { TableInstance, ColumnInstance } from 'react-table';
import MaterialHead from '@mui/material/TableHead';
import MaterialRow from '@mui/material/TableRow';
import MaterialCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TableContext } from '../../../particles';

const baseHeaderCellSx = {
  fontSize: 12,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  fontWeight: 700,
  color: 'rgba(15, 23, 42, 0.75)',
  padding: '14px 18px',
  borderBottom: '2px solid rgba(15, 23, 42, 0.12)',
  whiteSpace: 'nowrap'
};

export type Order = `asc` | `desc`;

export interface TableHeadProps {
  name: TableType;
  /** Table instance */
  tableInstance: TableInstance<TableRow>;
  /** Relays back to the main table component the current height of the header */
  setHeaderHeight: React.Dispatch<React.SetStateAction<number>>;
  /** When true, headers are draggable and sort changes call onSortChange */
  persistColumnPrefs?: boolean;
  /** Sort to hydrate on mount when prefs persistence is on */
  persistedSort?: TablePrefsSort | null;
  /** Called when user drags column headers to reorder; receives the new order */
  onOrderChange?: (newOrder: string[]) => void;
  /** Called when user clicks a header to sort; null means clear sort */
  onSortChange?: (sort: TablePrefsSort | null) => void;
}

// Reusable per-table dispatch — used by both click-to-sort and the persisted-sort
// hydration effect below. Returns the dispatched orderBy string for use by callers.
const useDispatchSort = (name: TableType) => {
  const {
    dispatch,
    state: {
      unifiedUsersPagination,
      coreUsersPagination,
      coreAccountsPagination,
      coreContactsPagination,
      coreOfficesPagination,
      teamPagination,
      billingPagination,
      accountPerformancePagination,
      billingAccountPagination,
      openAiPagination,
      twilioPagination,
      srtPagination,
      companyTransactionsLogsPagination,
      zazuAuditLogsPagination,
      apiKeys,
      artifactsPagination
    }
  } = useContext(TableContext);

  return React.useCallback(
    (orderBy: string) => {
      const dispatchSet = (key: TableSetReducerAction['key'], perPage: number) =>
        dispatch({ type: `set`, key, value: { page: 1, perPage, orderBy } });

      switch (name) {
        case 'unified-users':
          return dispatchSet('unifiedUsersPagination', unifiedUsersPagination.perPage);
        case 'core_offices':
          return dispatchSet('coreOfficesPagination', coreOfficesPagination.perPage);
        case 'core_accounts':
          return dispatchSet('coreAccountsPagination', coreAccountsPagination.perPage);
        case 'core_users':
          return dispatchSet('coreUsersPagination', coreUsersPagination.perPage);
        case 'core_contacts':
          return dispatchSet('coreContactsPagination', coreContactsPagination.perPage);
        case 'team':
          return dispatchSet('teamPagination', teamPagination.perPage);
        case 'billing':
          return dispatchSet('billingPagination', billingPagination.perPage);
        case 'account_performance':
          return dispatchSet('accountPerformancePagination', accountPerformancePagination.perPage);
        case 'billing-accounts':
          return dispatchSet('billingAccountPagination', billingAccountPagination.perPage);
        case 'open-ai':
          return dispatchSet('openAiPagination', openAiPagination.perPage);
        case 'twilio':
          return dispatchSet('twilioPagination', twilioPagination.perPage);
        case 'srt':
          return dispatchSet('srtPagination', srtPagination.perPage);
        case 'company-transactions-logs':
          return dispatchSet(
            'companyTransactionsLogsPagination',
            companyTransactionsLogsPagination.perPage
          );
        case 'api-keys':
          return dispatchSet('apiKeys', apiKeys.perPage);
        case 'zazu-audit-logs':
          return dispatchSet('zazuAuditLogsPagination', zazuAuditLogsPagination.perPage);
        case 'artifacts':
          return dispatchSet('artifactsPagination', artifactsPagination.perPage);
      }
    },
    [
      dispatch,
      name,
      unifiedUsersPagination.perPage,
      coreOfficesPagination.perPage,
      coreAccountsPagination.perPage,
      coreUsersPagination.perPage,
      coreContactsPagination.perPage,
      teamPagination.perPage,
      billingPagination.perPage,
      accountPerformancePagination.perPage,
      billingAccountPagination.perPage,
      openAiPagination.perPage,
      twilioPagination.perPage,
      srtPagination.perPage,
      companyTransactionsLogsPagination.perPage,
      zazuAuditLogsPagination.perPage,
      apiKeys.perPage,
      artifactsPagination.perPage
    ]
  );
};

interface SortableHeaderCellProps {
  id: string;
  enabled: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  sortableLabel?: React.ReactNode;
  sx: object;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  id,
  enabled,
  children,
  onClick,
  sx
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !enabled
  });

  const style = enabled
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
      }
    : {};

  return (
    <MaterialCell ref={setNodeRef as unknown as React.Ref<HTMLTableCellElement>} sx={sx} style={style} {...attributes}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {enabled && (
          <Box
            {...listeners}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'grab',
              color: 'rgba(15, 23, 42, 0.4)',
              '&:hover': { color: 'rgba(15, 23, 42, 0.75)' },
              '&:active': { cursor: 'grabbing' }
            }}
            aria-label="Drag to reorder column">
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Box
          onClick={onClick}
          sx={{ flex: 1, cursor: onClick ? 'pointer' : 'default', userSelect: 'none' }}>
          {children}
        </Box>
      </Box>
    </MaterialCell>
  );
};

export const TableHead: React.FC<TableHeadProps> = ({
  tableInstance,
  setHeaderHeight,
  name,
  persistColumnPrefs = false,
  persistedSort,
  onOrderChange,
  onSortChange
}: TableHeadProps) => {
  const { allColumns, headerGroups, setColumnOrder, visibleColumns } = tableInstance;
  const ref = useRef<HTMLTableSectionElement>(null);

  const [isSortedDesc, setIsSortedDesc] = React.useState<boolean>(true);
  const [orderBy, setOrderBy] = React.useState<string>('');

  const dispatchSort = useDispatchSort(name);

  // Hydrate sort once when persisted prefs arrive.
  const sortHydratedRef = useRef(false);
  useEffect(() => {
    if (!persistColumnPrefs || sortHydratedRef.current) return;
    if (!persistedSort) {
      sortHydratedRef.current = true;
      return;
    }
    const column = allColumns.find((c) => c.dbKey === persistedSort.dbKey);
    if (!column) {
      sortHydratedRef.current = true;
      return;
    }
    const orderByStr =
      persistedSort.direction === 'desc' ? `-${column.dbKey}` : column.dbKey;
    setOrderBy(String(column.id));
    setIsSortedDesc(persistedSort.direction === 'desc');
    dispatchSort(orderByStr);
    sortHydratedRef.current = true;
  }, [persistColumnPrefs, persistedSort, allColumns, dispatchSort]);

  const handleHeaderClick = (columnId: string, index: number) => {
    let newOrderBy = orderBy;
    let newIsSortedDesc = isSortedDesc;

    if (!orderBy || newOrderBy !== columnId) {
      newOrderBy = columnId;
      newIsSortedDesc = true;
    } else if (newIsSortedDesc) {
      newIsSortedDesc = false;
    } else {
      newOrderBy = ``;
    }

    setOrderBy(newOrderBy);
    setIsSortedDesc(newIsSortedDesc);

    const dbKey = allColumns[index].dbKey;
    const orderByStr = newOrderBy ? (newIsSortedDesc ? `-${dbKey}` : dbKey) : ``;
    dispatchSort(orderByStr);

    if (persistColumnPrefs && onSortChange) {
      onSortChange(
        newOrderBy
          ? { dbKey, direction: newIsSortedDesc ? 'desc' : 'asc' }
          : null
      );
    }
  };

  // DnD sensors — pointer for mouse / touch, keyboard for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const draggableIds = React.useMemo(
    () =>
      visibleColumns
        .filter((c) => c.id !== '_selector' && c.id !== 'action')
        .map((c) => String(c.id)),
    [visibleColumns]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const currentIds = visibleColumns
      .filter((c) => c.id !== '_selector')
      .map((c) => String(c.id));
    const oldIndex = currentIds.indexOf(String(active.id));
    const newIndex = currentIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const newOrder = arrayMove(currentIds, oldIndex, newIndex);
    setColumnOrder(newOrder);
    onOrderChange?.(newOrder);
  };

  useLayoutEffect(() => {
    setHeaderHeight(ref.current ? ref.current.clientHeight : 0);
  }, [setHeaderHeight]);

  const renderHeaderContent = (column: ColumnInstance<TableRow>) => {
    const isSorted = orderBy === column.id;
    // Direction must be per-column: only the active column reflects the current
    // sort state. Inactive columns default to 'asc' so their latent hover icon
    // doesn't flip when the active column toggles.
    const direction: Order = isSorted && isSortedDesc ? `desc` : `asc`;
    const sortTooltip = isSorted
      ? isSortedDesc
        ? `Sorted descending`
        : `Sorted ascending`
      : `Click to sort`;

    if (column.isSortable) {
      return (
        <Tooltip title={sortTooltip} arrow>
          <TableSortLabel
            sx={{
              fontSize: 12,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontWeight: 700,
              '& .MuiTableSortLabel-icon': { opacity: 0.7 }
            }}
            active={isSorted}
            direction={direction}>
            {column.render(`Header`)}
          </TableSortLabel>
        </Tooltip>
      );
    }
    return <>{column.render(`Header`)}</>;
  };

  return (
    <MaterialHead ref={ref}>
      {headerGroups.map((headerGroup) => {
        const { key, ...getHeaderGroupProps } = headerGroup.getHeaderGroupProps();

        const rowContent = headerGroup.headers.map((column, index) => {
          const { key: cellKey } = column.getHeaderProps();
          const columnId = String(column.id);
          const isDraggable =
            persistColumnPrefs && columnId !== '_selector' && columnId !== 'action';
          const cellSx = column.isSortable
            ? { ...baseHeaderCellSx, color: 'rgba(15, 23, 42, 0.85)' }
            : baseHeaderCellSx;
          const onCellClick = column.isSortable
            ? () => handleHeaderClick(columnId, index)
            : undefined;

          if (isDraggable) {
            return (
              <SortableHeaderCell
                key={cellKey}
                id={columnId}
                enabled
                onClick={onCellClick}
                sx={cellSx}>
                {renderHeaderContent(column)}
              </SortableHeaderCell>
            );
          }

          if (column.isSortable) {
            return (
              <MaterialCell
                key={cellKey}
                onClick={onCellClick}
                sx={{ ...cellSx, cursor: 'pointer', userSelect: 'none' }}>
                {renderHeaderContent(column)}
              </MaterialCell>
            );
          }

          return (
            <MaterialCell sx={cellSx} key={cellKey}>
              {renderHeaderContent(column)}
            </MaterialCell>
          );
        });

        if (persistColumnPrefs) {
          return (
            <MaterialRow key={key} {...getHeaderGroupProps}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}>
                <SortableContext items={draggableIds} strategy={horizontalListSortingStrategy}>
                  {rowContent}
                </SortableContext>
              </DndContext>
            </MaterialRow>
          );
        }

        return (
          <MaterialRow key={key} {...getHeaderGroupProps}>
            {rowContent}
          </MaterialRow>
        );
      })}
    </MaterialHead>
  );
};

export default TableHead;
